import { useReducer, useEffect, useMemo } from 'react';
import { appReducer, initialState, actionTypes } from '../state/appReducer';
import type { AppState } from '../state/types';

const PERSISTED_SETTINGS: (keyof AppState)[] = [
  'textSize',
  'reduceMotion',
  'highContrast',
  'darkMode',
  'soundEnabled',
  'vibrationEnabled',
  'language',
];

function initFromStorage(init: AppState): AppState {
  try {
    const stored = JSON.parse(
      localStorage.getItem('appSettings') || '{}'
    ) as Partial<AppState>;
    const persisted: Partial<AppState> = {};
    for (const key of PERSISTED_SETTINGS) {
      const v = stored[key];
      if (v !== undefined) {
        // cast here so TS knows you really want to assign string|boolean|number
        (persisted as any)[key] = v;
      }
    }
    return { ...init, ...persisted };
  } catch {
    return init;
  }
}

/**
 * React hook for managing global application state with persistence.
 *
 * Provides state and action dispatchers for accessibility and user
 * preferences. Settings are synchronized with localStorage and
 * reflected in the document element.
 *
 * @returns {{state: AppState, actions: Record<string, Function>}}
 *   Current state and bound action creators.
 */
export default function useAppState() {
  // <-- drop all generics here so TS picks the 3-arg overload correctly
  const [state, dispatch] = useReducer(
    appReducer,
    initialState,
    initFromStorage
  );

  // keep <html lang> in sync
  useEffect(() => {
    document.documentElement.lang = state.language;
  }, [state.language]);


  // toggle dark mode
  useEffect(() => {
    document.documentElement.classList.toggle('dark', state.darkMode);
  }, [state.darkMode]);

  // toggle high contrast mode
  useEffect(() => {
    document.documentElement.classList.toggle('high-contrast', state.highContrast);
  }, [state.highContrast]);

  // persist accessibility/settings
  useEffect(() => {
    try {
      const toStore: Partial<AppState> = {};
      for (const key of PERSISTED_SETTINGS) {
        (toStore as any)[key] = state[key];
      }
      localStorage.setItem('appSettings', JSON.stringify(toStore));
    } catch {
      /* ignore write errors */
    }
  }, [
    state.textSize,
    state.reduceMotion,
    state.highContrast,
    state.darkMode,
    state.soundEnabled,
    state.vibrationEnabled,
    state.language,
  ]);

  const actions = useMemo(
    () => ({
      setTextSize: (size: string) =>
        dispatch({ type: actionTypes.SET_TEXT_SIZE, payload: size }),
      toggleReduceMotion: () =>
        dispatch({ type: actionTypes.TOGGLE_REDUCE_MOTION }),
      toggleHighContrast: () =>
        dispatch({ type: actionTypes.TOGGLE_HIGH_CONTRAST }),
      toggleDarkMode: () => dispatch({ type: actionTypes.TOGGLE_DARK_MODE }),
      toggleSound: () => dispatch({ type: actionTypes.TOGGLE_SOUND }),
      toggleVibration: () => dispatch({ type: actionTypes.TOGGLE_VIBRATION }),
      setLanguage: (lang: string) =>
        dispatch({ type: actionTypes.SET_LANGUAGE, payload: lang }),
      setUserRole: (role: string | null) =>
        dispatch({ type: actionTypes.SET_USER_ROLE, payload: role }),
      /**
       * Async version for setting user role with loading/error states
       * @param rolePromise - Promise that resolves to user role
       * @returns Promise that resolves when operation completes
       */
      setUserRoleAsync: async (rolePromise: Promise<string | null>) => {
        dispatch({ type: actionTypes.SET_USER_ROLE_PENDING });
        try {
          const role = await rolePromise;
          dispatch({ type: actionTypes.SET_USER_ROLE_SUCCESS, payload: role });
        } catch (error: any) {
          dispatch({ type: actionTypes.SET_USER_ROLE_FAILURE, payload: error.message });
        }
      },
      setCurrentSimulation: (id: string | null) =>
        dispatch({ type: actionTypes.SET_CURRENT_SIMULATION, payload: id }),
      /**
       * Async version for setting current simulation with loading states
       * @param simulationPromise - Promise that resolves to simulation ID
       * @returns Promise that resolves when operation completes
       */
      setCurrentSimulationAsync: async (simulationPromise: Promise<string | null>) => {
        dispatch({ type: actionTypes.SET_CURRENT_SIMULATION_PENDING });
        try {
          const id = await simulationPromise;
          dispatch({ type: actionTypes.SET_CURRENT_SIMULATION_SUCCESS, payload: id });
        } catch (error: any) {
          dispatch({ type: actionTypes.SET_CURRENT_SIMULATION_FAILURE, payload: error.message });
        }
      },
      updateGlobalScore: (score: number) =>
        dispatch({ type: actionTypes.UPDATE_GLOBAL_SCORE, payload: score }),
      /**
       * Async version for updating global score (e.g., syncing with server)
       * @param scorePromise - Promise that resolves to score increment
       * @returns Promise that resolves when operation completes
       */
      updateGlobalScoreAsync: async (scorePromise: Promise<number>) => {
        dispatch({ type: actionTypes.UPDATE_GLOBAL_SCORE_PENDING });
        try {
          const score = await scorePromise;
          dispatch({ type: actionTypes.UPDATE_GLOBAL_SCORE_SUCCESS, payload: score });
        } catch (error: any) {
          dispatch({ type: actionTypes.UPDATE_GLOBAL_SCORE_FAILURE, payload: error.message });
        }
      },
      resetState: () => dispatch({ type: actionTypes.RESET_STATE }),
    }),
    []
  );

  return { state, actions };
}

export { actionTypes };
